import type { JSX } from 'react';
import {
  NO_AVAILABILITY_DATA,
  formatAvailability,
  type Availability,
  type DatabaseSize,
  type FlappingNode,
  type NetworkOverview,
  type OverviewEvent,
  type PlatformAgent,
  type PlatformOverview,
} from '@netdeck/contracts';
import type { TFunction } from 'i18next';
import { formatBytes } from '../../shared/format/bytes';
import { currentLanguage, useTranslation } from '../../shared/i18n';
import { messageText } from '../../shared/i18n/message';
import { useErrorText } from '../../shared/i18n/useErrorText';
import { formatAgo, formatMoment } from '../../shared/format/time';
import { DataTable, type Column } from '../../shared/ui/DataTable';
import { EmptyState, ErrorState, Loading, Panel } from '../../shared/ui/Panel';
import { StatusDot, type Status } from '../../shared/ui/StatusDot';
import { useNetworkOverview, usePlatformOverview } from './api';
import { StatTile } from './StatTile';

export interface OverviewPageProps {
  /** Есть право читать узлы — показывается блок «Сеть». */
  canSeeNetwork: boolean;
  /** Есть право на площадки — показывается блок «Платформа». */
  canSeePlatform: boolean;
  /** Переход на карту площадки: щелчок по узлу из ленты. */
  onOpenSite?: (siteId: string) => void;
}

/**
 * Обзор (§11, фаза 9, задача 1).
 *
 * Стартовой страницей остаётся карта: обзор отвечает на вопрос «как дела
 * вообще», а работают на карте. Поэтому он и не грузится отдельным куском —
 * это две таблицы и десяток чисел, а не React Flow.
 *
 * Блок, на который нет права, не показывается вовсе, а не показывается
 * пустым: «ноль узлов не в сети» и «вам не положено это видеть» выглядели бы
 * одинаково, а означают противоположное.
 */
export function OverviewPage({
  canSeeNetwork,
  canSeePlatform,
  onOpenSite,
}: OverviewPageProps): JSX.Element {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const language = currentLanguage();

  const network = useNetworkOverview(canSeeNetwork);
  const platform = usePlatformOverview(canSeePlatform);

  if (!canSeeNetwork && !canSeePlatform) {
    return <EmptyState title={t('overview.unavailable')} hint={t('overview.unavailableHint')} />;
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      {canSeeNetwork ? (
        <Panel
          title={t('overview.network.title')}
          subtitle={
            network.data
              ? t('overview.network.computedAt', {
                  at: formatMoment(network.data.computedAt, language),
                })
              : undefined
          }
        >
          {network.isPending ? (
            <Loading text={t('overview.network.loading')} />
          ) : network.error ? (
            <ErrorState
              title={t('overview.network.failed')}
              hint={t('overview.network.failedHint', { reason: errorText(network.error) })}
            />
          ) : (
            <NetworkBlock data={network.data} onOpenSite={onOpenSite} />
          )}
        </Panel>
      ) : null}

      {canSeePlatform ? (
        <Panel
          title={t('overview.platform.title')}
          subtitle={
            platform.data
              ? t('overview.platform.subtitle', {
                  version: platform.data.api.version,
                  uptime: formatUptime(platform.data.api.uptimeSec, t),
                })
              : undefined
          }
        >
          {platform.isPending ? (
            <Loading text={t('overview.platform.loading')} />
          ) : platform.error ? (
            <ErrorState
              title={t('overview.platform.failed')}
              hint={t('overview.platform.failedHint', { reason: errorText(platform.error) })}
            />
          ) : (
            <PlatformBlock data={platform.data} />
          )}
        </Panel>
      ) : null}
    </div>
  );
}

// — Сеть ————————————————————————————————————————————————

function NetworkBlock({
  data,
  onOpenSite,
}: {
  data: NetworkOverview;
  onOpenSite?: (siteId: string) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const language = currentLanguage();

  const { nodes, monitors, availability } = data;

  return (
    <div className="flex flex-col gap-3 p-3.5">
      <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-2">
        <StatTile
          label={t('overview.tile.nodesTotal')}
          value={nodes.total}
          hint={t('overview.tile.monitorsEnabled', {
            enabled: monitors.enabled,
            count: monitors.total,
          })}
        />
        <StatTile
          label={t('overview.tile.down')}
          value={nodes.down}
          tone={nodes.down > 0 ? 'down' : 'normal'}
          hint={
            nodes.down > 0
              ? t('overview.tile.checksFailing', { count: monitors.down })
              : t('overview.tile.allAnswering')
          }
        />
        <StatTile
          label={t('overview.tile.warn')}
          value={nodes.warn}
          tone={nodes.warn > 0 ? 'warn' : 'normal'}
          hint={t('overview.tile.warnHint')}
        />
        <StatTile
          label={t('overview.tile.unknown')}
          value={nodes.unknown}
          hint={
            nodes.withoutMonitors > 0
              ? t('overview.tile.withoutMonitors', { count: nodes.withoutMonitors })
              : t('overview.tile.noChecksYet')
          }
        />
      </div>

      <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-2">
        <StatTile
          label={t('overview.tile.availabilityDay')}
          value={availabilityText(availability.day, t)}
          hint={checksHint(availability.day.totalCount, t)}
        />
        <StatTile
          label={t('overview.tile.availabilityWeek')}
          value={availabilityText(availability.week, t)}
          hint={checksHint(availability.week.totalCount, t)}
        />
        <StatTile
          label={t('overview.tile.availabilityMonth')}
          value={availabilityText(availability.month, t)}
          hint={checksHint(availability.month.totalCount, t)}
        />
      </div>

      <div className="grid grid-cols-[repeat(auto-fit,minmax(340px,1fr))] items-start gap-3">
        <Subsection title={t('overview.flapping.title')}>
          <DataTable
            columns={flappingColumns(t)}
            rows={data.flapping}
            rowKey={(row) => row.nodeId}
            onRowClick={onOpenSite ? (row) => onOpenSite(row.siteId) : undefined}
            empty={
              <EmptyState
                title={t('overview.flapping.empty')}
                hint={t('overview.flapping.emptyHint')}
              />
            }
          />
        </Subsection>

        <Subsection title={t('overview.events.title')}>
          <DataTable
            columns={eventColumns(t, language)}
            rows={data.events}
            rowKey={(row) => row.id}
            rowDanger={(row) => row.toStatus === 'down'}
            // Лента длиннее соседнего списка «мигающих» узлов и растягивала
            // обзор на экран вниз; десять последних событий — то, ради чего
            // на неё смотрят, остальное доступно прокруткой.
            maxRows={10}
            onRowClick={onOpenSite ? (row) => onOpenSite(row.siteId) : undefined}
            empty={
              <EmptyState title={t('events.emptyTitle')} hint={t('overview.events.emptyHint')} />
            }
          />
        </Subsection>
      </div>
    </div>
  );
}

const flappingColumns = (t: TFunction): Column<FlappingNode>[] => [
  {
    key: 'node',
    header: t('events.column.node'),
    width: '1.3fr',
    render: (row) => <span className="truncate font-medium">{row.nodeName}</span>,
  },
  {
    key: 'site',
    header: t('sites.one'),
    width: '1fr',
    render: (row) => <span className="truncate text-muted">{row.siteName}</span>,
  },
  {
    key: 'down',
    header: t('overview.flapping.downs'),
    width: '0.5fr',
    render: (row) => <span className="font-mono text-down-fg">{row.downCount}</span>,
  },
  {
    key: 'transitions',
    header: t('overview.flapping.transitions'),
    width: '0.5fr',
    render: (row) => <span className="font-mono text-muted">{row.transitionCount}</span>,
  },
];

const eventColumns = (t: TFunction, language: string): Column<OverviewEvent>[] => [
  {
    key: 'status',
    header: '',
    width: '20px',
    render: (row) => (
      <StatusDot
        status={toStatus(row.toStatus)}
        label={`${row.nodeName}: ${row.fromStatus} → ${row.toStatus}`}
      />
    ),
  },
  {
    key: 'at',
    header: t('events.column.at'),
    width: '0.8fr',
    render: (row) => <span className="font-mono text-muted">{formatMoment(row.at, language)}</span>,
  },
  {
    key: 'node',
    header: t('events.column.node'),
    width: '1.1fr',
    render: (row) => <span className="truncate font-medium">{row.nodeName}</span>,
  },
  {
    key: 'site',
    header: t('sites.one'),
    width: '1fr',
    render: (row) => <span className="truncate text-muted">{row.siteName}</span>,
  },
  {
    key: 'transition',
    header: t('events.column.transition'),
    width: '0.9fr',
    render: (row) => (
      <span className={`font-mono ${row.toStatus === 'down' ? 'text-down-fg' : 'text-muted'}`}>
        {row.fromStatus} → {row.toStatus}
      </span>
    ),
  },
];

// — Платформа ————————————————————————————————————————————

function PlatformBlock({ data }: { data: PlatformOverview }): JSX.Element {
  const { t } = useTranslation();
  const language = currentLanguage();

  const online = data.agents.filter((agent) => agent.status === 'online').length;
  const lost = data.agents.filter((agent) => agent.status === 'offline').length;

  return (
    <div className="flex flex-col gap-3 p-3.5">
      <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-2">
        <StatTile
          label={t('overview.tile.agentsOnline')}
          value={`${online} / ${data.agents.length}`}
          tone={lost > 0 ? 'down' : 'normal'}
          hint={
            lost > 0
              ? t('overview.tile.agentsLost', { count: lost })
              : t('overview.tile.allSitesOnline')
          }
        />
        <StatTile
          label={t('overview.tile.vncSessions')}
          value={data.sessions.vnc}
          hint={t('overview.tile.openNow')}
        />
        <StatTile
          label={t('overview.tile.videoStreams')}
          value={data.sessions.videoLive}
          hint={t('overview.tile.archiveCount', { count: data.sessions.videoArchive })}
        />
        <StatTile
          label={t('overview.tile.backup')}
          value={
            data.backup.latest
              ? formatAgo(Math.round(data.backup.latest.ageHours * 3600), t)
              : t('common.dash')
          }
          tone={backupTone(data)}
          hint={
            data.backup.latest
              ? `${formatBytes(data.backup.latest.sizeBytes, t)} · ${data.backup.latest.name}`
              : data.backup.problem === null
                ? t('overview.tile.backupUnknown')
                : messageText(data.backup.problem, t)
          }
        />
        <StatTile
          label={t('overview.tile.databaseSize')}
          value={
            data.database.problem === null
              ? formatBytes(data.database.totalBytes, t)
              : t('common.dash')
          }
          hint={describeDatabase(data.database, t)}
        />
      </div>

      <Subsection title={t('overview.dependencies')}>
        <div className="flex flex-wrap gap-2 px-3 py-2.5">
          <DependencyChip name="Postgres" state={data.dependencies.database} />
          <DependencyChip name="Redis" state={data.dependencies.redis} />
          <DependencyChip name="go2rtc" state={data.dependencies.go2rtc} />
        </div>
      </Subsection>

      {data.database.largest.length > 0 ? (
        <Subsection title={t('overview.databaseTables')}>
          <div className="flex flex-col gap-1 px-3 py-2.5">
            {data.database.largest.map((table) => (
              <div key={table.name} className="flex items-center gap-2.5 text-sm">
                <span className="w-[180px] flex-none truncate font-mono text-xs">{table.name}</span>
                {/*
                  Полоса длиной в долю от самой большой таблицы. Не от всей
                  базы: служебные таблицы TimescaleDB в список не входят,
                  и доли от целого не сложились бы в сто процентов — полоски
                  выглядели бы обрубками без объяснения.
                */}
                <span className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-overlay">
                  <span
                    className="block h-full rounded-full bg-line-strong"
                    style={{ width: `${String(shareOfLargest(data.database, table.sizeBytes))}%` }}
                  />
                </span>
                <span className="w-[80px] flex-none text-right font-mono text-xs text-dim">
                  {formatBytes(table.sizeBytes, t)}
                </span>
              </div>
            ))}
          </div>
        </Subsection>
      ) : null}

      <Subsection title={t('overview.siteAgents')}>
        <DataTable
          columns={agentColumns(t, language)}
          rows={data.agents}
          rowKey={(row) => row.id}
          rowDanger={(row) => row.status === 'offline'}
          empty={<EmptyState title={t('overview.noAgents')} hint={t('overview.noAgentsHint')} />}
        />
      </Subsection>
    </div>
  );
}

const agentColumns = (t: TFunction, language: string): Column<PlatformAgent>[] => [
  {
    key: 'status',
    header: '',
    width: '20px',
    render: (row) => <StatusDot status={agentStatus(row)} label={agentStatusLabel(row, t)} />,
  },
  {
    key: 'name',
    header: t('agents.column.name'),
    width: '1.2fr',
    render: (row) => <span className="truncate font-medium">{row.name}</span>,
  },
  {
    key: 'site',
    header: t('agents.column.sites'),
    width: '1fr',
    render: (row) => {
      const names = row.sites.map((site) => site.name).join(', ');

      return (
        <span className="truncate text-muted" title={names || undefined}>
          {names || <span className="text-dim">{t('agents.noSites')}</span>}
        </span>
      );
    },
  },
  {
    key: 'version',
    header: t('agents.column.version'),
    width: '0.7fr',
    render: (row) => <span className="font-mono text-muted">{row.version ?? '—'}</span>,
  },
  {
    key: 'platform',
    header: t('agents.column.platform'),
    width: '0.8fr',
    render: (row) => <span className="font-mono text-dim">{row.platform ?? '—'}</span>,
  },
  {
    key: 'uptime',
    header: t('overview.column.uptime'),
    width: '0.8fr',
    render: (row) => (
      <span className="font-mono text-muted">
        {row.uptimeSec === null ? t('common.dash') : formatUptime(row.uptimeSec, t)}
      </span>
    ),
  },
  {
    key: 'lastSeen',
    header: t('overview.column.lastSeen'),
    width: '0.9fr',
    render: (row) => (
      <span className={`font-mono ${row.status === 'offline' ? 'text-down-fg' : 'text-dim'}`}>
        {row.lastSeenAt === null
          ? t('users.neverLoggedIn')
          : formatMoment(row.lastSeenAt, language)}
      </span>
    ),
  },
];

function DependencyChip({
  name,
  state,
}: {
  name: string;
  state: PlatformOverview['dependencies']['database'];
}): JSX.Element {
  const { t } = useTranslation();

  return (
    <span
      className="flex items-center gap-1.5 rounded-sm border border-line-strong px-2.5 py-1 text-sm"
      title={
        state.error === null
          ? t('overview.dependency.answeredIn', { ms: state.latencyMs })
          : messageText(state.error ?? '', t)
      }
    >
      <StatusDot
        status={state.status === 'up' ? 'up' : 'down'}
        label={`${name}: ${state.status}`}
      />
      <span className="font-medium">{name}</span>
      <span className={`font-mono text-xs ${state.status === 'up' ? 'text-dim' : 'text-down-fg'}`}>
        {state.status === 'up'
          ? t('overview.dependency.ms', { ms: Math.round(state.latencyMs) })
          : t('overview.dependency.silent')}
      </span>
    </span>
  );
}

// — Мелочи ————————————————————————————————————————————————

function Subsection({ title, children }: { title: string; children: JSX.Element }): JSX.Element {
  return (
    <section className="flex min-w-0 flex-col overflow-hidden rounded-sm border border-line-subtle">
      <header className="flex h-7 flex-none items-center border-b border-line-subtle bg-raised px-3 font-mono text-2xs tracking-[0.06em] text-faint uppercase">
        {title}
      </header>
      {children}
    </section>
  );
}

function checksHint(total: number, t: TFunction): string {
  return total === 0 ? t('overview.noChecks') : t('common.count.checks', { count: total });
}

/**
 * Доступность в процентах или «нет данных».
 *
 * `formatAvailability` из контрактов возвращает либо готовое число
 * с процентом, либо ключ словаря — за периодом без единой проверки
 * стоит не ноль процентов, а отсутствие данных, и это разные вещи.
 */
function availabilityText(availability: Availability, t: TFunction): string {
  const text = formatAvailability(availability);

  return text === NO_AVAILABILITY_DATA ? t(text) : text;
}

function toStatus(value: OverviewEvent['toStatus']): Status {
  return value === 'up' ? 'up' : value === 'down' ? 'down' : 'unknown';
}

function agentStatus(agent: PlatformAgent): Status {
  if (agent.status === 'online') {
    return 'up';
  }
  // «Ни разу не подключался» — это не авария: агента завели, но ещё
  // не запустили. Красная точка тут звала бы чинить то, что не сломано.
  return agent.status === 'offline' ? 'down' : 'unknown';
}

function agentStatusLabel(agent: PlatformAgent, t: TFunction): string {
  if (agent.status === 'online') {
    return t('overview.agent.online', { name: agent.name });
  }
  return agent.status === 'offline'
    ? t('overview.agent.lost', { name: agent.name })
    : t('overview.agent.never', { name: agent.name });
}

/**
 * Подпись под объёмом базы.
 *
 * Доля телеметрии здесь не украшение: у неё есть политики хранения
 * (14 суток сырых точек, 400 агрегата), и «база выросла вдвое» означает
 * разное в зависимости от того, что именно выросло. Телеметрия растёт
 * до предела хранения и останавливается; всё остальное растёт без предела.
 */
export function describeDatabase(database: DatabaseSize, t: TFunction): string {
  if (database.problem !== null) {
    return messageText(database.problem, t);
  }

  if (database.totalBytes === 0) {
    return t('overview.database.empty');
  }

  const share = Math.round((database.telemetryBytes / database.totalBytes) * 100);

  return t('overview.database.telemetryShare', {
    size: formatBytes(database.telemetryBytes, t),
    share,
  });
}

/**
 * Длина полосы: доля от самой большой таблицы в списке.
 *
 * Именно от самой большой, а не от всей базы: в список не входят служебные
 * таблицы TimescaleDB и индексы каталога, и доли от целого не сложились бы
 * в сто процентов. Полоса здесь отвечает на вопрос «что во сколько раз
 * больше соседа», а не «какую часть диска занимает».
 */
export function shareOfLargest(database: DatabaseSize, sizeBytes: number): number {
  const biggest = database.largest[0]?.sizeBytes ?? 0;

  if (biggest === 0) {
    return 0;
  }

  // Не меньше двух процентов: полоса нулевой ширины читается как «нет
  // данных», а таблица в списке есть и место занимает.
  return Math.max(2, Math.round((sizeBytes / biggest) * 100));
}

function backupTone(data: PlatformOverview): 'normal' | 'down' | 'warn' {
  if (!data.backup.configured) {
    // Не настроено — это не сбой платформы, а незакрытая задача установки.
    return 'warn';
  }
  if (!data.backup.latest) {
    return 'down';
  }
  // Сутки — шаг расписания из README. Копия старше двух суток означает,
  // что расписание не отработало дважды подряд.
  return data.backup.latest.ageHours > 48 ? 'down' : 'normal';
}

/**
 * Время работы: сутки, часы, минуты. Секунды показываются только первую
 * минуту — дальше они меняются быстрее, чем человек успевает прочитать.
 */
export function formatUptime(seconds: number, t: TFunction): string {
  const total = Math.max(0, Math.floor(seconds));

  if (total < 60) {
    return t('overview.uptime.seconds', { count: total });
  }

  const days = Math.floor(total / 86_400);
  const hours = Math.floor((total % 86_400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);

  if (days > 0) {
    return t('overview.uptime.days', { days, hours });
  }
  if (hours > 0) {
    return t('overview.uptime.hours', { hours, minutes });
  }
  return t('overview.uptime.minutes', { count: minutes });
}
